import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { BrandingProvider } from "@/context/BrandingContext";
import { ThemeProvider } from "@/context/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Companies from "@/pages/Companies";
import Users from "@/pages/Users";
import Recipients from "@/pages/Recipients";
import Branches from "@/pages/Branches";
import Drivers from "@/pages/Drivers";
import RoutesPage from "@/pages/Routes";
import Warehouses from "@/pages/Warehouses";
import VerifyEmail from "@/pages/VerifyEmail";
import ConfirmEmailChange from "@/pages/ConfirmEmailChange";
import Profile from "@/pages/Profile";
import PublicPayInvoice from "@/pages/PublicPayInvoice";
import PublicReceiptView from "@/pages/PublicReceiptView";
import Manifests from "@/pages/Manifests";
import ManifestDetail from "@/pages/ManifestDetail";
import Packages from "@/pages/Packages";
import UnclaimedPackages from "@/pages/UnclaimedPackages";
import PackageDetail from "@/pages/PackageDetail";
import Invoices from "@/pages/Invoices";
import Pickups from "@/pages/Pickups";
import AuthCallback from "@/pages/AuthCallback";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AuditLog from "@/pages/AuditLog";
import Billing from "@/pages/Billing";
import Promotions from "@/pages/Promotions";
import DriverAssignments from "@/pages/DriverAssignments";
import DomainSettings from "@/pages/DomainSettings";
import AdminCustomDomains from "@/pages/AdminCustomDomains";
import Communication from "@/pages/Communication";
import CampaignDetail from "@/pages/CampaignDetail";
import ZipExpressSettings from "@/pages/ZipExpressSettings";
import ZipExpressRevenue from "@/pages/ZipExpressRevenue";
import Landing from "@/pages/Landing";
import SignupCourier from "@/pages/SignupCourier";
import SignupCustomer from "@/pages/SignupCustomer";
import CourierLogin from "@/pages/CourierLogin";
import DriverSignup from "@/pages/DriverSignup";
import DriverVerifyEmail from "@/pages/DriverVerifyEmail";
import DriverPortal from "@/pages/DriverPortal";
import LiveMap from "@/pages/LiveMap";
import PublicTrack from "@/pages/PublicTrack";
import SetPassword from "@/pages/SetPassword";
import ApiDocs from "@/pages/ApiDocs";

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Landing />;
  if (user.role === "driver") return <Navigate to="/driver/portal" replace />;
  // Force the first-login password change before any other route
  if (user.must_change_password) return <Navigate to="/set-password" replace />;
  return <Navigate to="/dashboard" replace />;
}

/**
 * Blocks every protected route until the signed-in user clears their
 * `must_change_password` flag. Auto-provisioned campaign recipients land here
 * the first time they sign in with their temporary password. Only /set-password
 * and a logout link are reachable until they pick a permanent password.
 */
function PasswordChangeGuard({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (!user || !user.must_change_password) return children;
  if (location.pathname.startsWith("/set-password")) return children;
  return <Navigate to="/set-password" replace />;
}

/**
 * When a company is account_locked (unpaid initial subscription invoice), restrict
 * non-super-admin users to /billing and /verify-email only. Redirect any other route.
 */
function LockedAccountGuard({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  if (!user) return children;
  const locked = user.account_locked && user.role !== "super_admin";
  if (!locked) return children;
  const allowedPaths = ["/billing", "/verify-email"];
  const isAllowed = allowedPaths.some((p) => location.pathname.startsWith(p));
  if (isAllowed) return children;
  // Only company_admin can actually open /billing — redirecting other roles
  // there would bounce them back here forever (infinite redirect loop).
  if (user.role !== "company_admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50 px-4" data-testid="account-locked-page">
        <div className="w-full max-w-md bg-white border border-zinc-200 rounded-2xl shadow-sm p-8 text-center">
          <h1 className="text-xl font-semibold text-zinc-900">Account temporarily unavailable</h1>
          <p className="text-sm text-zinc-600 mt-3">
            {user.company_name || "Your courier"}'s account is currently locked. Please contact your courier
            company to restore access.
          </p>
          <button
            onClick={async () => { await logout(); window.location.assign("/login"); }}
            className="mt-6 inline-flex items-center justify-center px-5 py-2.5 rounded-md border border-zinc-300 text-zinc-700 text-sm font-medium hover:bg-zinc-100 transition-colors"
            data-testid="locked-signout-btn"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }
  return <Navigate to="/billing" replace />;
}

/**
 * Drivers don't use the multi-tenant Layout — they have a dedicated mobile-first portal.
 * Redirect them to /driver/portal if they try to access any layout-wrapped route.
 */
function DriverPortalGuard({ children }) {
  const { user } = useAuth();
  if (user && user.role === "driver") return <Navigate to="/driver/portal" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Synchronously handle Emergent OAuth callback in URL fragment
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup/courier" element={<SignupCourier />} />
      <Route path="/c/:slug/signup" element={<SignupCustomer />} />
      <Route path="/c/:slug/login" element={<CourierLogin />} />
      <Route path="/c/:slug/track" element={<PublicTrack />} />
      <Route path="/c/:slug/track/:trackingNumber" element={<PublicTrack />} />
      <Route path="/driver/signup" element={<DriverSignup />} />
      <Route path="/driver/verify-email" element={<DriverVerifyEmail />} />
      <Route
        path="/driver/portal"
        element={
          <ProtectedRoute roles={["driver"]}>
            <DriverPortal />
          </ProtectedRoute>
        }
      />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/confirm-email-change" element={<ConfirmEmailChange />} />
      <Route path="/pay/:id" element={<PublicPayInvoice />} />
      <Route path="/receipt/:token" element={<PublicReceiptView />} />
      <Route path="/track" element={<PublicTrack />} />
      <Route path="/track/:trackingNumber" element={<PublicTrack />} />
      <Route path="/docs" element={<ApiDocs />} />
      <Route
        path="/set-password"
        element={
          <ProtectedRoute>
            <SetPassword />
          </ProtectedRoute>
        }
      />
      <Route
        element={
          <ProtectedRoute>
            <DriverPortalGuard>
              <PasswordChangeGuard>
                <LockedAccountGuard>
                  <Layout />
                </LockedAccountGuard>
              </PasswordChangeGuard>
            </DriverPortalGuard>
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/profile" element={<Profile />} />
        <Route
          path="/companies"
          element={
            <ProtectedRoute roles={["super_admin"]}>
              <Companies />
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin"]}>
              <Users />
            </ProtectedRoute>
          }
        />
        <Route path="/packages" element={<Packages />} />
        <Route path="/packages/:id" element={<PackageDetail />} />
        <Route
          path="/pickups"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher", "customer"]}>
              <Pickups />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recipients"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher", "warehouse"]}>
              <Recipients />
            </ProtectedRoute>
          }
        />
        <Route
          path="/branches"
          element={
            <ProtectedRoute>
              <Branches />
            </ProtectedRoute>
          }
        />
        <Route
          path="/drivers"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher"]}>
              <Drivers />
            </ProtectedRoute>
          }
        />
        <Route
          path="/routes"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher"]}>
              <RoutesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/live-map"
          element={
            <ProtectedRoute roles={["super_admin"]}>
              <LiveMap />
            </ProtectedRoute>
          }
        />
        <Route
          path="/warehouses"
          element={
            <ProtectedRoute>
              <Warehouses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manifests"
          element={
            <ProtectedRoute>
              <Manifests />
            </ProtectedRoute>
          }
        />
        <Route
          path="/manifests/:id"
          element={
            <ProtectedRoute>
              <ManifestDetail />
            </ProtectedRoute>
          }
        />
        <Route path="/invoices" element={<Invoices />} />
        <Route
          path="/audit"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin"]}>
              <AuditLog />
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin"]}>
              <Billing />
            </ProtectedRoute>
          }
        />
        <Route
          path="/driver-assignments"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher", "warehouse_super", "warehouse_super_l1", "warehouse_super_l2"]}>
              <DriverAssignments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/unclaimed"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher", "warehouse_super", "warehouse_super_l1", "warehouse_super_l2"]}>
              <UnclaimedPackages />
            </ProtectedRoute>
          }
        />
        <Route
          path="/promotions"
          element={
            <ProtectedRoute roles={["super_admin"]}>
              <Promotions />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/domain"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin"]}>
              <DomainSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/custom-domains"
          element={
            <ProtectedRoute roles={["super_admin"]}>
              <AdminCustomDomains />
            </ProtectedRoute>
          }
        />
        <Route
          path="/zipexpress"
          element={
            <ProtectedRoute roles={["super_admin"]}>
              <ZipExpressSettings />
            </ProtectedRoute>
          }
        />
        <Route
          path="/zipexpress/revenue"
          element={
            <ProtectedRoute roles={["super_admin"]}>
              <ZipExpressRevenue />
            </ProtectedRoute>
          }
        />
        <Route
          path="/communication"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher"]}>
              <Communication />
            </ProtectedRoute>
          }
        />
        <Route
          path="/communication/campaigns/:id"
          element={
            <ProtectedRoute roles={["super_admin", "company_admin", "dispatcher"]}>
              <CampaignDetail />
            </ProtectedRoute>
          }
        />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrandingProvider>
        <AuthProvider>
          <ThemeProvider>
            <BrowserRouter>
              <AppRouter />
            </BrowserRouter>
          </ThemeProvider>
        </AuthProvider>
      </BrandingProvider>
    </div>
  );
}

export default App;
